import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, OwnerOnlyBanner } from "./DashboardLayout";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, ShieldCheck, Inbox } from "lucide-react";
import { toast } from "sonner";
import { DEFAULT_ACCENT, brighten } from "@/lib/accent";

export default function SalesReview() {
  const { user } = useAuth();
  const accent = brighten(user?.accent_color || DEFAULT_ACCENT);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [recon, setRecon] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [note, setNote] = useState("");
  const REJECTION_REASONS = ["Duplicate", "Couldn't verify with carrier", "Customer canceled", "Suspected fraud", "Other"];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sales/pending-verification");
      setItems(data.items || []);
      api.get("/sales/reconciliation?days=30").then((r) => setRecon(r.data)).catch(() => {});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const verify = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/sales/${id}/verify`);
      toast.success("Sale verified.");
      setItems((prev) => prev.filter((s) => s.id !== id));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const doReject = async (reason) => {
    const id = rejecting;
    if (!id) return;
    setBusyId(id);
    setRejecting(null);
    try {
      await api.post(`/sales/${id}/reject`, { reason, note: note.trim() });
      toast.success("Sale rejected and points removed.");
      setItems((prev) => prev.filter((s) => s.id !== id));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
      setNote("");
    }
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
    catch { return iso; }
  };

  return (
    <div data-testid="sales-review-page">
      <PageHeader
        title="Sales Review"
        subtitle="Reconcile manually-logged sales before they count. Verify legitimate sales or reject unverifiable ones."
      />
      <OwnerOnlyBanner note="Manually-entered sales stay unverified until reconciled here. CSV/SARA Plus imports are auto-verified." />

      {recon ? (
        <div className="grid grid-cols-4 gap-3 mt-5" data-testid="recon-strip">
          {[
            { n: recon.verified, l: "VERIFIED", c: "#34C759" },
            { n: recon.rejected, l: "REJECTED", c: "#E13C32" },
            { n: recon.pending, l: "PENDING", c: "#fff" },
            { n: `${recon.verify_rate_pct}%`, l: "VERIFY RATE", c: accent },
          ].map((x) => (
            <div key={x.l} className="bg-[#0E0E11] border border-[#27272A] rounded-md p-3 text-center">
              <div className="font-display text-2xl" style={{ color: x.c }}>{x.n}</div>
              <div className="text-[10px] text-[#71717A] tracking-wider mt-1">{x.l}</div>
            </div>
          ))}
        </div>
      ) : null}

      <div className="mt-6">
        {loading ? (
          <div className="flex items-center gap-2 text-[#A1A1AA] py-16 justify-center" data-testid="sales-review-loading">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading pending sales…
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-[#27272A] rounded-md" data-testid="sales-review-empty">
            <ShieldCheck className="w-10 h-10 mx-auto mb-3" style={{ color: accent }} />
            <div className="font-display text-xl">ALL CLEAR</div>
            <p className="text-[#A1A1AA] text-sm mt-1">No manual sales are waiting for verification.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-[#A1A1AA] text-sm">
              <Inbox className="w-4 h-4" />
              <span data-testid="sales-review-count">{items.length} sale{items.length === 1 ? "" : "s"} pending verification</span>
            </div>
            {items.map((s) => (
              <div
                key={s.id}
                data-testid={`pending-sale-${s.id}`}
                className="flex items-center justify-between gap-4 p-4 bg-[#0E0E11] border border-[#27272A] rounded-md"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-label text-white truncate">{s.user_name || "Rep"}</div>
                    {s.aged ? (
                      <span data-testid={`aged-badge-${s.id}`} className="text-[10px] font-bold tracking-wide text-[#E13C32] border border-[#E13C32]/40 bg-[#E13C32]/15 rounded px-1.5 py-0.5">
                        AGED {s.age_days}d
                      </span>
                    ) : null}
                  </div>
                  <div className="text-xs text-[#A1A1AA] mt-0.5">
                    {s.lines} line{s.lines === 1 ? "" : "s"} · {(s.segment || "").toUpperCase()} · {fmtDate(s.created_at)}
                  </div>
                  <div className="text-[11px] text-[#71717A] mt-0.5">
                    Logged by {s.created_by_name || "—"}{s.notes ? ` · ${s.notes}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    size="sm"
                    onClick={() => verify(s.id)}
                    disabled={busyId === s.id}
                    className="bg-[#34C759] hover:bg-[#2fb350] text-white font-label rounded-sm"
                    data-testid={`verify-sale-${s.id}`}
                  >
                    {busyId === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1" /> Verify</>}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setNote(""); setRejecting(s.id); }}
                    disabled={busyId === s.id}
                    className="border-[#E13C32]/40 text-[#E13C32] hover:bg-[#E13C32]/10 font-label rounded-sm"
                    data-testid={`reject-sale-${s.id}`}
                  >
                    <XCircle className="w-4 h-4 mr-1" /> Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {rejecting ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setRejecting(null)} data-testid="reject-reason-modal">
          <div className="w-full max-w-md bg-[#0E0E11] border border-[#27272A] rounded-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="font-display text-xl">WHY REJECT THIS SALE?</div>
            <p className="text-sm text-[#A1A1AA] mt-1 mb-4">A reason is required — it's saved to the compliance trail.</p>
            <div className="space-y-2">
              {REJECTION_REASONS.map((r) => (
                <button
                  key={r}
                  data-testid={`reason-${r}`}
                  onClick={() => doReject(r)}
                  className="w-full text-left px-4 py-3 rounded-md border border-[#27272A] hover:border-[#E13C32]/60 hover:bg-[#E13C32]/10 text-white transition-colors"
                >
                  {r}
                </button>
              ))}
            </div>
            <input
              data-testid="reject-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional note (no customer PII)"
              className="w-full mt-4 px-3 py-2 bg-[#050506] border border-[#27272A] rounded-md text-white text-sm"
            />
            <button data-testid="reject-cancel" onClick={() => setRejecting(null)} className="w-full mt-3 py-2 text-[#A1A1AA] text-sm">
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
